import React, { useState, useEffect } from 'react';
import { Mail, Linkedin, Github, Phone, Code2, Brain, Workflow, ChevronDown, Menu, X } from 'lucide-react';

export default function Portfolio() {
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(id);
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-lg border-b border-gray-800' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="text-lg sm:text-xl font-bold">
              <span className="text-white">Personal </span>
              <span className="text-gray-400">Portfolio</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              {['Home', 'About', 'Skills', 'Experience', 'Projects', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-gray-400 hover:text-white transition-colors duration-300 text-sm font-medium"
                >
                  {item}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-400 p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-2 bg-gray-900/95 backdrop-blur-xl rounded-2xl p-4 border border-gray-800">
              {['Home', 'About', 'Skills', 'Experience', 'Projects', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="block w-full text-left text-gray-400 hover:text-white transition-colors duration-300 font-medium py-3 px-2 rounded-lg hover:bg-gray-800/50"
                >
                  {item}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center px-4 pt-20 pb-12 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10 w-full">
          <div className="mb-6 sm:mb-8 md:mb-12">
            <img 
              src="/Profile_dp.jpg" 
              alt="Ali Hussnain" 
              className="w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 mx-auto rounded-full object-cover border border-gray-800 shadow-xl grayscale hover:grayscale-0 transition-all duration-500"
            />
          </div>
          
          <div className="space-y-3 sm:space-y-4 md:space-y-6 mb-6 sm:mb-8 md:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight px-2">
              Ali <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-blue-500 bg-clip-text text-transparent">Hussnain</span>
            </h1>
            
            <div className="inline-block px-4">
              <p className="text-lg sm:text-xl md:text-2xl lg:text-4xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-gray-100 to-gray-400 bg-clip-text text-transparent">
                AI Agent Developer
              </p>
              <div className="h-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full"></div>
            </div>
          </div>
          
          <p className="text-sm sm:text-base md:text-lg lg:text-2xl text-gray-300 mb-8 sm:mb-10 md:mb-16 max-w-4xl mx-auto leading-relaxed font-light px-4 text-center">
            Transforming traditional applications into <span className="text-white font-semibold">intelligent, AI-powered solutions</span> that drive business growth and scalability.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 md:gap-6 mb-8 sm:mb-10 md:mb-16 px-4">
            <a href="mailto:hussnainjafari176@gmail.com" className="group w-full sm:w-auto px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold text-sm sm:text-base md:text-lg transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/50 hover:scale-105 flex items-center justify-center gap-2 sm:gap-3">
              <Mail size={20} className="sm:w-6 sm:h-6" />
              <span>Contact Me</span>
            </a>
            <button onClick={() => scrollToSection('projects')} className="w-full sm:w-auto px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-white/5 backdrop-blur-sm text-white border-2 border-white/20 rounded-xl font-semibold text-sm sm:text-base md:text-lg transition-all duration-300 hover:bg-white/10 hover:border-white/40 hover:scale-105 flex items-center justify-center gap-2 sm:gap-3">
              <Code2 size={20} className="sm:w-6 sm:h-6" />
              <span>View Projects</span>
            </button>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-3xl mx-auto w-full mb-8 sm:mb-10 md:mb-16 p-4 sm:p-6 md:p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl">
            <div className="text-center py-4 sm:py-0">
              <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent mb-2">2+</div>
              <div className="text-base sm:text-lg text-gray-400">Years Experience</div>
            </div>
            <div className="text-center py-4 sm:py-0 sm:border-x border-t sm:border-t-0 border-b sm:border-b-0 border-white/10">
              <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent mb-2">50+</div>
              <div className="text-base sm:text-lg text-gray-400">Projects Completed</div>
            </div>
            <div className="text-center py-4 sm:py-0">
              <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pink-400 to-purple-500 bg-clip-text text-transparent mb-2">100%</div>
              <div className="text-base sm:text-lg text-gray-400">Client Satisfaction</div>
            </div>
          </div>
          
          <div className="mt-12 sm:mt-16 md:mt-20 animate-bounce">
            <ChevronDown size={32} className="sm:w-10 sm:h-10 mx-auto text-gray-500" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-950/5 to-black"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-8 sm:mb-12 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 md:mb-6">
              About <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">Me</span>
            </h2>
            <div className="w-20 sm:w-24 h-1.5 sm:h-2 bg-gradient-to-r from-purple-600 to-blue-600 mx-auto rounded-full"></div>
          </div>
          
          <div className="bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 border border-purple-500/20 shadow-2xl shadow-purple-500/10">
            <p className="text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed mb-6 sm:mb-8 text-justify">
              AI Agent Developer with <span className="text-white font-semibold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">2+ years of experience</span> specializing in intelligent automation and agentic AI systems. I transform traditional applications into efficient, AI-powered solutions that drive business growth and scalability.
            </p>
            <p className="text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed mb-6 sm:mb-8 text-justify">
              My expertise spans AI workflow development, custom agent creation, and deploying sophisticated automation systems using both code-based frameworks <span className="text-purple-400 font-semibold">(LangChain, LangGraph, CrewAI)</span> and no-code platforms <span className="text-blue-400 font-semibold">(n8n, Zapier, Make.com)</span>.
            </p>
            <p className="text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed mb-8 sm:mb-12 text-justify">
              As a backend developer proficient in <span className="text-green-400 font-semibold">Spring Boot</span>, I build reliable, production-ready applications that seamlessly integrate AI capabilities.
            </p>
            
            <div className="mt-8 sm:mt-12 pt-8 sm:pt-12 border-t border-purple-500/20">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-semibold mb-6 sm:mb-8 md:mb-10 text-white flex items-center gap-3 sm:gap-4">
                <div className="w-1.5 sm:w-2 h-8 sm:h-10 md:h-12 bg-gradient-to-b from-purple-600 to-blue-600 rounded-full"></div>
                Education
              </h3>
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 p-6 sm:p-8 bg-black/40 rounded-2xl border border-purple-500/10 hover:border-purple-500/30 transition-all duration-300">
                <div className="bg-gradient-to-br from-purple-600 to-blue-600 p-4 sm:p-5 rounded-2xl shadow-lg shadow-purple-500/20">
                  <Code2 size={28} className="sm:w-9 sm:h-9 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-xl sm:text-2xl font-semibold text-white mb-2 sm:mb-3">BE Computer Engineering</h4>
                  <p className="text-lg sm:text-xl md:text-2xl text-gray-400 mb-1 sm:mb-2">University of Engineering and Technology Taxila</p>
                  <p className="text-base sm:text-lg md:text-xl text-gray-500">3rd Year • Expected Graduation: 2027</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 md:mb-6">
              Skills & <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">Expertise</span>
            </h2>
            <div className="w-20 sm:w-24 h-1.5 sm:h-2 bg-gradient-to-r from-purple-600 to-blue-600 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {[
              { title: 'AI & Automation', icon: Brain, items: ['Agentic AI Systems', 'AI Workflow Design', 'RAG Pipeline Development', 'Custom AI Agents', 'Multi-Platform Integration'], gradient: 'from-purple-600 to-pink-600' },
              { title: 'Frameworks & Tools', icon: Workflow, items: ['LangChain / LangGraph', 'CrewAI', 'n8n (Master Level)', 'Zapier & Make.com', 'ManyChat'], gradient: 'from-blue-600 to-cyan-600' },
              { title: 'AI Models & APIs', icon: Brain, items: ['OpenAI API (Master)', 'Claude', 'DeepSeek', 'Mistral', 'Gemini'], gradient: 'from-pink-600 to-purple-600' },
              { title: 'Programming', icon: Code2, items: ['Python ⭐⭐⭐⭐', 'Java ⭐⭐⭐', 'JavaScript ⭐⭐⭐', 'Spring Boot', 'RESTful APIs'], gradient: 'from-green-600 to-emerald-600' },
              { title: 'Backend Development', icon: Code2, items: ['Spring Boot Framework', 'RESTful API Development', 'Database Management', 'Docker & AWS'], gradient: 'from-orange-600 to-red-600' },
              { title: 'Specializations', icon: Workflow, items: ['Lead Generation Automation', 'Social Media Workflows', 'CRM Integration', 'Chatbot Development', 'Web Scraping'], gradient: 'from-indigo-600 to-purple-600' }
            ].map((skill, idx) => (
              <div key={idx} className="group bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-gray-800 hover:border-purple-500/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20">
                <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className={`p-3 sm:p-4 bg-gradient-to-br ${skill.gradient} rounded-xl sm:rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <skill.icon className="text-white w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{skill.title}</h3>
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {skill.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 sm:gap-3 text-gray-400 text-sm sm:text-base md:text-lg group-hover:text-gray-300 transition-colors">
                      <span className="text-purple-500 text-base sm:text-lg md:text-xl mt-0.5 sm:mt-1">▸</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-950/5 to-black"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-8 sm:mb-12 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 md:mb-6">
              Work <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">Experience</span>
            </h2>
            <div className="w-20 sm:w-24 h-1.5 sm:h-2 bg-gradient-to-r from-purple-600 to-blue-600 mx-auto rounded-full"></div>
          </div>
          
          <div className="space-y-6 sm:space-y-8">
            {[
              {
                title: 'AI Developer (Contract-Based)',
                company: 'Haze Vape (Iraqi E-commerce)',
                period: 'October 2025 - Present',
                color: 'from-purple-600 to-pink-600',
                points: [
                  'Providing AI Agent and automation development services for e-commerce operations',
                  'Building custom AI solutions to streamline business processes and enhance customer experience',
                  'Implementing intelligent workflows for inventory management, customer support, and marketing automation'
                ]
              },
              {
                title: 'AI Chatbot Developer',
                company: 'Spider Media Solutions',
                period: 'August 2024 - August 2025',
                color: 'from-blue-600 to-cyan-600',
                points: [
                  'Developed multiple chatbots for Facebook Messenger, Instagram, and WhatsApp platforms',
                  'Created AI-powered conversational agents for various online businesses, improving customer engagement by 40%',
                  'Implemented automated customer support solutions that reduced response time from hours to seconds'
                ]
              },
              {
                title: 'Lead Generation Intern',
                company: 'Spider Media Solutions',
                period: 'June 2024 - August 2024',
                color: 'from-pink-600 to-purple-600',
                points: [
                  'Created automated chatbots for lead collection, increasing lead capture rate by 60%',
                  'Developed workflows to capture and organize leads for outreach campaigns',
                  'Implemented automation systems that streamlined lead generation processes, saving 15+ hours per week'
                ]
              }
            ].map((exp, idx) => (
              <div key={idx} className="group bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 border border-gray-800 hover:border-purple-500/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-purple-500/20">
                <div className="flex flex-col space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                  <div className={`inline-block bg-gradient-to-r ${exp.color} px-4 sm:px-6 py-1.5 sm:py-2 rounded-full w-fit`}>
                    <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">{exp.title}</h3>
                  </div>
                  <p className="text-lg sm:text-xl md:text-2xl text-gray-300 font-medium">{exp.company}</p>
                  <span className="text-base sm:text-lg md:text-xl text-gray-400 font-medium">{exp.period}</span>
                </div>
                <ul className="space-y-3 sm:space-y-4">
                  {exp.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-gray-400 text-sm sm:text-base md:text-lg group-hover:text-gray-300 transition-colors">
                      <span className="text-purple-500 text-lg sm:text-xl md:text-2xl mt-0.5 sm:mt-1 flex-shrink-0">▸</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 md:mb-6">
              Featured <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">Projects</span>
            </h2>
            <div className="w-20 sm:w-24 h-1.5 sm:h-2 bg-gradient-to-r from-purple-600 to-blue-600 mx-auto rounded-full"></div>
          </div>
          
          {/* Project 1 */}
          <div className="bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 mb-6 sm:mb-8 md:mb-10 hover:shadow-2xl hover:shadow-purple-500/20">
            <div className="mb-6 sm:mb-8 md:mb-10">
              <span className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-semibold text-white mb-4 sm:mb-6">Featured Project</span>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight">
                AI-Powered Scrum Master Assistant
              </h3>
            </div>

            {/* Project Gallery - Flexible Image Grid */}
            <div className="mb-8 sm:mb-10 md:mb-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                {/* Add your project images here - uncomment and update paths */}
                {/* Example with 3 images */}
                
                {/* <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 aspect-video">
                  <img 
                    src="p1.png" 
                    alt="AI-Powered Scrum Master Dashboard" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
                    <p className="text-white text-sm sm:text-base font-semibold">Sprint Analysis Dashboard</p>
                  </div>
                </div>
                
                <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 aspect-video">
                  <img 
                    src="/project1-image-2.jpg" 
                    alt="Slack Integration" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
                    <p className="text-white text-sm sm:text-base font-semibold">Slack Notifications</p>
                  </div>
                </div>
                
                <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 aspect-video sm:col-span-2 lg:col-span-1">
                  <img 
                    src="/project1-image-3.jpg" 
                    alt="AI Analysis Report" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
                    <p className="text-white text-sm sm:text-base font-semibold">AI-Generated Insights</p>
                  </div>
                </div> */}
               
                
                {/* For single large image, use this instead */}
                
                <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 sm:col-span-2 lg:col-span-3">
                  <img 
                    src="p1.png" 
                    alt="AI-Powered Scrum Master Assistant" 
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
                    <p className="text-white text-base sm:text-lg font-semibold">Complete Scrum Master Workflow</p>
                  </div>
                </div>
               
                
                {/* For 2 images side by side */}
                {/* 
                <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 aspect-video sm:col-span-1 lg:col-span-1.5">
                  <img 
                    src="/project1-image-1.jpg" 
                    alt="Project Screenshot 1" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                
                <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 aspect-video sm:col-span-1 lg:col-span-1.5">
                  <img 
                    src="/project1-image-2.jpg" 
                    alt="Project Screenshot 2" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                */}
              </div>
            </div>

            <div className="space-y-6 sm:space-y-8 md:space-y-10">
              {/* Background */}
              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-purple-600 to-blue-600 rounded-full"></div>
                  Background
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  Agile teams often struggle with fragmented information across multiple platforms (Asana, Slack, team updates), making it challenging for Scrum Masters to identify impediments and support sprint delivery effectively. Manual data gathering from these sources is time-consuming and prone to missing critical warning signals.
                </p>
              </div>

              {/* What I Built */}
              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-purple-600 to-blue-600 rounded-full"></div>
                  What I Built
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  An intelligent automation system that simulates the analytical aspects of a Scrum Master role by collecting and analyzing data from multiple sources—Asana project management, Slack communications, and direct developer responses—to provide comprehensive sprint status analysis and identify areas requiring intervention.
                </p>
              </div>

              {/* How It Works */}
              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-purple-600 to-blue-600 rounded-full"></div>
                  How It Works
                </h4>
                <div className="bg-black/50 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-10 space-y-3 sm:space-y-4 md:space-y-6 border border-purple-500/20">
                  {[
                    { num: '1', title: 'Data Collection', desc: 'The workflow automatically retrieves project structure, sections, and task organization from Asana, providing AI with complete context of the team\'s work.' },
                    { num: '2', title: 'Task Monitoring', desc: 'Tracks recently modified tasks in Asana, detecting changes in status, assignments, and updates to identify potential delays or blockers.' },
                    { num: '3', title: 'Communication Analysis', desc: 'Collects and analyzes team conversations from Slack channels, identifying warning signals, concerns, or areas requiring attention from discussion patterns.' },
                    { num: '4', title: 'Direct Developer Input', desc: 'Gathers daily scrum responses from developers about sprint progress, impediments, and support needs.' },
                    { num: '5', title: 'AI Analysis', desc: 'All collected data is processed by an AI model within Scrum methodology context to identify impediments, areas requiring intervention, support recommendations, and warning signals regarding Sprint Goal achievement.' },
                    { num: '6', title: 'Actionable Output', desc: 'Results are pushed to Slack channel for team visibility and can be used by subsequent workflow iterations for continuous monitoring.' }
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3 sm:gap-4 md:gap-5 group hover:translate-x-2 transition-transform duration-300">
                      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-bold min-w-[40px] sm:min-w-[50px] text-center text-base sm:text-lg shadow-lg flex-shrink-0">{step.num}</div>
                      <p className="text-sm sm:text-base text-gray-300"><span className="font-semibold text-white text-base sm:text-lg">{step.title}:</span> {step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Technical Highlights */}
              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-purple-600 to-blue-600 rounded-full"></div>
                  Technical Highlights
                </h4>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: 'Multi-Source Integration', desc: 'Seamlessly connects Asana, Slack, and custom data collection endpoints in a unified workflow' },
                    { title: 'Context-Aware AI Processing', desc: 'Utilizes OpenAI with custom prompts designed around Scrum methodology principles for accurate analysis' },
                    { title: 'Flexible Architecture', desc: 'Modular design allows easy addition of new data sources or modification of analysis parameters' },
                    { title: 'Automated Scheduling', desc: 'Can be triggered on-click or scheduled to run on workdays for consistent monitoring' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300 hover:text-white transition-colors">
                      <span className="text-purple-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Outcome */}
              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-purple-600 to-blue-600 rounded-full"></div>
                  Outcome
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 sm:mb-6 text-justify">
                  The automation serves as an intelligent support system that enhances the Scrum Master's effectiveness while respecting that AI cannot replace human intuition, empathy, and experience. It provides:
                </p>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: 'Time Savings', desc: 'Reduces data gathering time from hours to minutes, allowing Scrum Masters to focus on facilitation and team support' },
                    { title: 'Early Detection', desc: 'Identifies potential sprint impediments earlier through continuous monitoring and pattern recognition' },
                    { title: 'Comprehensive Insights', desc: 'Provides holistic view of sprint health by correlating data from multiple sources that might be missed in manual analysis' },
                    { title: 'Team Transparency', desc: 'Increases team visibility into sprint status and challenges through automated reporting' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300">
                      <span className="text-purple-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tech Stack */}
              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-purple-600 to-blue-600 rounded-full"></div>
                  Tech Stack
                </h4>
                <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4">
                  {['n8n', 'OpenAI API', 'Slack API', 'Asana API', 'Workflow Automation', 'AI Analysis'].map((tech) => (
                    <span key={tech} className="px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold text-white hover:scale-105 transition-transform duration-300 hover:shadow-lg hover:shadow-purple-500/30">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Project 2: Auto-Generate Social Media Posts */}
          <div className="bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 mb-6 sm:mb-8 md:mb-10 hover:shadow-2xl hover:shadow-purple-500/20">
            <div className="mb-6 sm:mb-8 md:mb-10">
              <span className="inline-block bg-gradient-to-r from-blue-600 to-cyan-600 px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-semibold text-white mb-4 sm:mb-6">Featured Project</span>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight">
                Auto-Generate Social Media Posts from URLs with AI
              </h3>
            </div>

            {/* Project Gallery */}
            <div className="mb-8 sm:mb-10 md:mb-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                {/* Add your project images here - uncomment and update paths */}
                
                {/* For single large image, use this instead */}
                
                <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 sm:col-span-2 lg:col-span-3">
                  <img 
                    src="p2.png" 
                    alt="AI-Powered Scrum Master Assistant" 
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
                    <p className="text-white text-base sm:text-lg font-semibold">Complete Scrum Master Workflow</p>
                  </div>
                </div>
               
               
              </div>
            </div>

            <div className="space-y-6 sm:space-y-8 md:space-y-10">
              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-blue-600 to-cyan-600 rounded-full"></div>
                  Background
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  Content creators and marketing teams struggle with maintaining consistent social media presence across multiple platforms. Creating platform-specific posts manually is time-consuming and requires different content strategies for Twitter, Facebook, LinkedIn, and Instagram. The challenge intensifies when trying to convert article URLs into engaging social media content while maintaining brand voice and maximizing engagement.
                </p>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-blue-600 to-cyan-600 rounded-full"></div>
                  What I Built
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  An intelligent n8n workflow that automatically transforms any URL into platform-optimized social media posts. The system leverages AI to analyze content, generate engaging copy with relevant hashtags, create accompanying images, and distribute posts across multiple social networks—all with Telegram integration for review and approval before publishing.
                </p>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-blue-600 to-cyan-600 rounded-full"></div>
                  How It Works
                </h4>
                <div className="bg-black/50 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-10 space-y-3 sm:space-y-4 md:space-y-6 border border-purple-500/20">
                  {[
                    { num: '1', title: 'URL Input', desc: 'The workflow accepts a URL containing the content to be shared across social media platforms.' },
                    { num: '2', title: 'Content Extraction', desc: 'Automatically scrapes and analyzes the webpage content, extracting key information, themes, and main topics.' },
                    { num: '3', title: 'AI Content Generation', desc: 'Uses OpenAI to generate platform-specific posts optimized for Twitter, Facebook, LinkedIn, and Instagram with appropriate tone and length.' },
                    { num: '4', title: 'Image Creation', desc: 'Generates eye-catching visuals using AI image generation to accompany the posts and increase engagement.' },
                    { num: '5', title: 'Hashtag Optimization', desc: 'Intelligently suggests relevant hashtags based on content analysis to maximize reach and discoverability.' },
                    { num: '6', title: 'Telegram Review', desc: 'Sends generated content to Telegram for review and approval, allowing for final adjustments before publishing.' },
                    { num: '7', title: 'Multi-Platform Publishing', desc: 'Automatically posts approved content to all configured social media platforms simultaneously.' }
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3 sm:gap-4 md:gap-5 group hover:translate-x-2 transition-transform duration-300">
                      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-bold min-w-[40px] sm:min-w-[50px] text-center text-base sm:text-lg shadow-lg flex-shrink-0">{step.num}</div>
                      <p className="text-sm sm:text-base text-gray-300"><span className="font-semibold text-white text-base sm:text-lg">{step.title}:</span> {step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-blue-600 to-cyan-600 rounded-full"></div>
                  Technical Highlights
                </h4>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: 'Multi-Platform API Integration', desc: 'Seamlessly connects with Twitter, Facebook, LinkedIn, and Instagram APIs for automated posting' },
                    { title: 'AI-Powered Content Adaptation', desc: 'Tailors content length, tone, and style for each platform\'s unique requirements and audience' },
                    { title: 'Telegram Bot Integration', desc: 'Provides real-time content preview and approval workflow through Telegram messenger' },
                    { title: 'Automated Image Generation', desc: 'Creates platform-optimized visuals without manual design work' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300 hover:text-white transition-colors">
                      <span className="text-blue-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-blue-600 to-cyan-600 rounded-full"></div>
                  Outcome
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 sm:mb-6 text-justify">
                  The automation dramatically streamlines social media content distribution while maintaining quality and platform optimization. It provides:
                </p>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: 'Time Efficiency', desc: 'Reduces content creation time from hours to minutes, enabling consistent posting schedule' },
                    { title: 'Platform Optimization', desc: 'Ensures each post is perfectly tailored for its target platform\'s best practices and audience' },
                    { title: 'Brand Consistency', desc: 'Maintains consistent messaging across all platforms while adapting to each platform\'s style' },
                    { title: 'Increased Reach', desc: 'Maximizes engagement through AI-optimized hashtags and platform-specific content strategies' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300">
                      <span className="text-blue-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-blue-600 to-cyan-600 rounded-full"></div>
                  Tech Stack
                </h4>
                <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4">
                  {['n8n', 'OpenAI API', 'Telegram Bot API', 'Twitter API', 'Facebook API', 'LinkedIn API', 'Instagram API', 'AI Image Generation', 'Web Scraping'].map((tech) => (
                    <span key={tech} className="px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/30 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold text-white hover:scale-105 transition-transform duration-300 hover:shadow-lg hover:shadow-blue-500/30">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Project 3: Google Maps Scraper */}
          <div className="bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 mb-6 sm:mb-8 md:mb-10 hover:shadow-2xl hover:shadow-purple-500/20">
            <div className="mb-6 sm:mb-8 md:mb-10">
              <span className="inline-block bg-gradient-to-r from-green-600 to-emerald-600 px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-semibold text-white mb-4 sm:mb-6">Featured Project</span>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight">
                Google Maps Scraper with AI-Powered Lead Generation
              </h3>
            </div>

            {/* Project Gallery */}
            <div className="mb-8 sm:mb-10 md:mb-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                {/* Add your project images here - uncomment and update paths */}
                {/* 
                   {/* For single large image, use this instead */}
                
                <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 sm:col-span-2 lg:col-span-3">
                  <img 
                    src="p3.png" 
                    alt="AI-Powered Scrum Master Assistant" 
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
                    <p className="text-white text-base sm:text-lg font-semibold">Complete Scrum Master Workflow</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6 sm:space-y-8 md:space-y-10">
              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-green-600 to-emerald-600 rounded-full"></div>
                  Background
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  Sales and marketing teams spend countless hours manually researching potential leads, searching for businesses in specific locations, and crafting personalized outreach messages. Traditional lead generation methods are time-consuming, prone to errors, and don't scale effectively. Finding the right businesses, gathering contact information, and creating compelling outreach messages individually for hundreds of prospects is simply not feasible.
                </p>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-green-600 to-emerald-600 rounded-full"></div>
                  What I Built
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  An automated n8n workflow that scrapes Google Maps for businesses based on location and category, extracts comprehensive business information, and uses AI to generate personalized cold outreach messages for each prospect. The system transforms hours of manual research into minutes of automated, intelligent lead generation ready for immediate use in sales campaigns.
                </p>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-green-600 to-emerald-600 rounded-full"></div>
                  How It Works
                </h4>
                <div className="bg-black/50 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-10 space-y-3 sm:space-y-4 md:space-y-6 border border-purple-500/20">
                  {[
                    { num: '1', title: 'Target Definition', desc: 'User specifies the target location (city, region, or area) and business category or industry to focus the lead search.' },
                    { num: '2', title: 'Google Maps Scraping', desc: 'The workflow systematically searches Google Maps and extracts business listings matching the specified criteria.' },
                    { num: '3', title: 'Data Extraction', desc: 'Collects comprehensive information including business name, address, phone number, website, ratings, reviews, and business hours.' },
                    { num: '4', title: 'Data Enrichment', desc: 'Organizes and structures the extracted data, removing duplicates and validating contact information for accuracy.' },
                    { num: '5', title: 'AI Outreach Generation', desc: 'Uses OpenAI to analyze each business and generate personalized cold outreach messages tailored to their industry and specific business details.' },
                    { num: '6', title: 'Export & Integration', desc: 'Outputs structured data with contact details and outreach messages, ready for immediate import into CRM systems or email campaigns.' }
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3 sm:gap-4 md:gap-5 group hover:translate-x-2 transition-transform duration-300">
                      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-bold min-w-[40px] sm:min-w-[50px] text-center text-base sm:text-lg shadow-lg flex-shrink-0">{step.num}</div>
                      <p className="text-sm sm:text-base text-gray-300"><span className="font-semibold text-white text-base sm:text-lg">{step.title}:</span> {step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-green-600 to-emerald-600 rounded-full"></div>
                  Technical Highlights
                </h4>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: 'Intelligent Web Scraping', desc: 'Advanced scraping techniques that efficiently extract data from Google Maps while respecting rate limits' },
                    { title: 'Data Validation & Cleaning', desc: 'Automated processes to ensure data quality, remove duplicates, and validate contact information' },
                    { title: 'AI Personalization Engine', desc: 'OpenAI integration that crafts unique outreach messages based on each business\'s specific characteristics' },
                    { title: 'Scalable Architecture', desc: 'Handles bulk lead generation across multiple locations and industries simultaneously' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300 hover:text-white transition-colors">
                      <span className="text-green-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-green-600 to-emerald-600 rounded-full"></div>
                  Outcome
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 sm:mb-6 text-justify">
                  The automation revolutionizes B2B lead generation by eliminating manual research and enabling scalable, personalized outreach. It delivers:
                </p>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: 'Massive Time Savings', desc: 'Reduces lead research time from days to hours, generating hundreds of qualified leads with personalized messages automatically' },
                    { title: 'Higher Quality Leads', desc: 'Provides comprehensive business data enabling more informed and targeted sales approaches' },
                    { title: 'Personalized at Scale', desc: 'Combines mass lead generation with individualized outreach messages for each prospect' },
                    { title: 'Increased Conversion Rates', desc: 'AI-generated personalized messages significantly improve response rates compared to generic templates' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300">
                      <span className="text-green-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-green-600 to-emerald-600 rounded-full"></div>
                  Tech Stack
                </h4>
                <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4">
                  {['n8n', 'Google Maps API', 'Web Scraping', 'OpenAI API', 'Data Processing', 'Lead Generation', 'CRM Integration', 'Data Validation'].map((tech) => (
                    <span key={tech} className="px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold text-white hover:scale-105 transition-transform duration-300 hover:shadow-lg hover:shadow-green-500/30">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Project 4: Quantitative/Qualitative Check AI Agent */}
          <div className="bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 mb-6 sm:mb-8 md:mb-10 hover:shadow-2xl hover:shadow-purple-500/20">
            <div className="mb-6 sm:mb-8 md:mb-10">
              <span className="inline-block bg-gradient-to-r from-orange-600 to-red-600 px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-semibold text-white mb-4 sm:mb-6">Featured Project</span>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight">
                Quantitative/Qualitative Check AI Agent
              </h3>
            </div>

            {/* Project Gallery - 2 Images Per Row */}
<div className="mb-8 sm:mb-10 md:mb-12">
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
    
    {/* Image 1 */}
    <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 aspect-video">
      <img 
        src="s1.png" 
        alt="Project Screenshot 1" 
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
        <p className="text-white text-sm sm:text-base font-semibold">Dashboard View</p>
      </div>
    </div>
    
    {/* Image 2 */}
    <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 aspect-video">
      <img 
        src="s2.png" 
        alt="Project Screenshot 2" 
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
        <p className="text-white text-sm sm:text-base font-semibold">Analytics View</p>
      </div>
    </div>
    
    {/* Image 3 - Will appear on next row */}
    <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 aspect-video">
      <img 
        src="s3.png" 
        alt="Project Screenshot 3" 
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
        <p className="text-white text-sm sm:text-base font-semibold">Reports</p>
      </div>
    </div>

        {/* Image 5 - Will appear on next row */}
    <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 aspect-video">
      <img 
        src="s5.png" 
        alt="Project Screenshot 3" 
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
        <p className="text-white text-sm sm:text-base font-semibold">Reports</p>
      </div>
    </div>
        {/* Image 6 - Will appear on next row */}
    <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 aspect-video">
      <img 
        src="s6.png" 
        alt="Project Screenshot 3" 
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
        <p className="text-white text-sm sm:text-base font-semibold">Reports</p>
      </div>
    </div>
    
           {/* Image 7 - Will appear on next row */}
    <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 aspect-video">
      <img 
        src="s7.png" 
        alt="Project Screenshot 3" 
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 sm:p-6">
        <p className="text-white text-sm sm:text-base font-semibold">Reports</p>
      </div>
    </div>
    {/* Add more images here - they will automatically flow 2 per row */}
    
  </div>
</div>

            <div className="space-y-6 sm:space-y-8 md:space-y-10">
              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-orange-600 to-red-600 rounded-full"></div>
                  Background
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  Educational institutions face significant challenges in maintaining quality and consistency across course materials. Faculty members must manually validate that all required files and folders are present, ensure questions align with Bloom's Taxonomy levels, and verify solution accuracy—a process that is extremely time-consuming, error-prone, and difficult to scale across multiple courses and semesters. Manual validation of hundreds of academic files can take weeks.
                </p>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-orange-600 to-red-600 rounded-full"></div>
                  What I Built
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  A comprehensive desktop AI agent built with JavaFX and powered by Gemini CLI that performs both structural validation (Quantitative Check) and content analysis (Qualitative Check). The system automates the entire validation process for academic materials, from verifying folder structures to evaluating questions against Bloom's Taxonomy and assessing solution quality—providing detailed reports with ratings and recommendations.
                </p>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-orange-600 to-red-600 rounded-full"></div>
                  How It Works
                </h4>
                <div className="bg-black/50 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-10 space-y-3 sm:space-y-4 md:space-y-6 border border-purple-500/20">
                  {[
                    { num: '1', title: 'Dual Folder Input', desc: 'User provides two folders: the Actual Folder (to be validated) and Sample Folder (reference structure with required files).' },
                    { num: '2', title: 'Quantitative Validation', desc: 'System compares folder structures, identifies missing files/folders, and displays their expected directory paths for easy correction.' },
                    { num: '3', title: 'File Analysis & Filtering', desc: 'Identifies and filters relevant course materials: assignments, quizzes, midterms, finals, distinguishing between question and solution files.' },
                    { num: '4', title: 'Bloom\'s Taxonomy Evaluation', desc: 'AI analyzes each question to verify alignment with specified Bloom\'s Taxonomy levels, rating questions out of 10 with improvement recommendations.' },
                    { num: '5', title: 'Solution Assessment', desc: 'Evaluates solution files for validity and relevance, rating each solution out of 10 with constructive enhancement suggestions.' },
                    { num: '6', title: 'Report Generation', desc: 'Compiles comprehensive reports for each file, summarizing quality, ratings, and actionable recommendations for both questions and solutions.' }
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3 sm:gap-4 md:gap-5 group hover:translate-x-2 transition-transform duration-300">
                      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-bold min-w-[40px] sm:min-w-[50px] text-center text-base sm:text-lg shadow-lg flex-shrink-0">{step.num}</div>
                      <p className="text-sm sm:text-base text-gray-300"><span className="font-semibold text-white text-base sm:text-lg">{step.title}:</span> {step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-orange-600 to-red-600 rounded-full"></div>
                  Technical Highlights
                </h4>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: 'Dual-Check System', desc: 'Combines structural validation with deep content analysis for comprehensive quality assurance' },
                    { title: 'AI-Powered Analysis', desc: 'Utilizes OpenAI RAG model and Gemini CLI for sophisticated educational content evaluation' },
                    { title: 'Fuzzy Matching', desc: 'Implements similarity search algorithms to detect minor discrepancies and file variations' },
                    { title: 'Professional Desktop UI', desc: 'JavaFX interface provides intuitive, responsive design for efficient workflow management' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300 hover:text-white transition-colors">
                      <span className="text-orange-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-orange-600 to-red-600 rounded-full"></div>
                  Outcome
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 sm:mb-6 text-justify">
                  The desktop agent transforms academic quality assurance from a weeks-long manual process into an automated, consistent, and insightful workflow. It provides:
                </p>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: 'Dramatic Time Reduction', desc: 'Reduces validation time from weeks to hours, automating both structural and content checks across entire course materials' },
                    { title: 'Consistency & Accuracy', desc: 'Eliminates human error in validation and ensures consistent evaluation standards across all materials' },
                    { title: 'Actionable Insights', desc: 'Provides detailed ratings and specific recommendations for improving question quality and solution accuracy' },
                    { title: 'Scalable Quality Assurance', desc: 'Enables educational institutions to maintain high standards across multiple courses and departments efficiently' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300">
                      <span className="text-orange-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-orange-600 to-red-600 rounded-full"></div>
                  Tech Stack
                </h4>
                <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4">
                  {['Java', 'JavaFX', 'Gemini CLI', 'OpenAI RAG', 'Fuzzy-Wuzzy Library', 'File Processing', 'Desktop Application', 'AI Analysis'].map((tech) => (
                    <span key={tech} className="px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/30 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold text-white hover:scale-105 transition-transform duration-300 hover:shadow-lg hover:shadow-orange-500/30">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Project 5: WhatsApp Hospital Appointment Chatbot */}
          <div className="bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 mb-6 sm:mb-8 md:mb-10 hover:shadow-2xl hover:shadow-purple-500/20">
            <div className="mb-6 sm:mb-8 md:mb-10">
              <span className="inline-block bg-gradient-to-r from-pink-600 to-purple-600 px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-semibold text-white mb-4 sm:mb-6">Featured Project</span>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight">
                WhatsApp Hospital Appointment Chatbot
              </h3>
            </div>

{/* Project Video Gallery - Single Video */}
<div className="mb-8 sm:mb-10 md:mb-12">
  <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 max-w-4xl mx-auto">
    <video 
      className="w-full h-auto rounded-xl sm:rounded-2xl"
      controls
      autoPlay
      muted
      loop
      playsInline
      poster="/project-thumbnail.jpg"
      style={{ maxHeight: '500px', objectFit: 'contain' }}
    >
      <source src="whatsapp_chatbot.mp4" type="video/mp4" />
      <source src="/project-demo.webm" type="video/webm" />
      Your browser does not support the video tag.
    </video>
  </div>
</div>

            <div className="space-y-6 sm:space-y-8 md:space-y-10">
              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-pink-600 to-purple-600 rounded-full"></div>
                  Background
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  Hospitals face constant challenges with appointment management, patient inquiries, and providing 24/7 support. Traditional phone-based systems are overwhelmed during peak hours, patients struggle to find the right specialist for their symptoms, and managing medical history while providing personalized advice requires significant staff resources. Patients need instant access to healthcare services without long wait times or complex navigation systems.
                </p>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-pink-600 to-purple-600 rounded-full"></div>
                  What I Built
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  An intelligent WhatsApp chatbot built on ManyChat that revolutionizes hospital appointment management and patient care. The system provides 24/7 automated appointment booking, AI-powered doctor recommendations based on symptoms, secure medical history tracking, and multilingual medical query assistance—all through the familiar WhatsApp interface that patients already use daily.
                </p>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-pink-600 to-purple-600 rounded-full"></div>
                  How It Works
                </h4>
                <div className="bg-black/50 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-10 space-y-3 sm:space-y-4 md:space-y-6 border border-purple-500/20">
                  {[
                    { num: '1', title: 'Patient Initiation', desc: 'Patients start conversation with the hospital WhatsApp number, triggering the automated chatbot system.' },
                    { num: '2', title: 'Doctor Directory Access', desc: 'Chatbot displays complete list of available doctors with their specializations, qualifications, and availability schedules.' },
                    { num: '3', title: 'Smart Recommendations', desc: 'AI analyzes patient-reported symptoms and medical concerns to suggest the most relevant specialist doctors.' },
                    { num: '4', title: 'Appointment Booking', desc: 'Shows real-time available slots for selected doctors, allowing instant appointment booking with automatic confirmation.' },
                    { num: '5', title: 'Medical History Integration', desc: 'Securely retrieves patient medical history from Google Sheets to provide context-aware, personalized medical advice.' },
                    { num: '6', title: 'AI Medical Assistant', desc: 'OpenAI integration answers medical queries with accurate information in the patient\'s preferred language.' },
                    { num: '7', title: 'Automated Reminders', desc: 'Sends appointment reminders via WhatsApp notifications and email to ensure patients never miss appointments.' }
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3 sm:gap-4 md:gap-5 group hover:translate-x-2 transition-transform duration-300">
                      <div className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-bold min-w-[40px] sm:min-w-[50px] text-center text-base sm:text-lg shadow-lg flex-shrink-0">{step.num}</div>
                      <p className="text-sm sm:text-base text-gray-300"><span className="font-semibold text-white text-base sm:text-lg">{step.title}:</span> {step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-pink-600 to-purple-600 rounded-full"></div>
                  Technical Highlights
                </h4>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: 'No-Code Platform Power', desc: 'Built on ManyChat enabling rapid development and easy maintenance without extensive coding' },
                    { title: 'AI Recommendation Engine', desc: 'Bootsheet integration implements sophisticated algorithms for symptom-based doctor matching' },
                    { title: 'Secure Data Management', desc: 'Google Sheets backend provides HIPAA-compliant storage for patient records and appointment data' },
                    { title: 'Multilingual AI Support', desc: 'OpenAI integration enables natural conversation and accurate medical information in multiple languages' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300 hover:text-white transition-colors">
                      <span className="text-pink-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-pink-600 to-purple-600 rounded-full"></div>
                  Outcome
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 sm:mb-6 text-justify">
                  The chatbot transforms patient experience and hospital operations by providing instant, intelligent healthcare access. It delivers:
                </p>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: '24/7 Availability', desc: 'Patients can book appointments, ask medical questions, and access services anytime without waiting for office hours' },
                    { title: 'Reduced Staff Workload', desc: 'Automates routine appointment booking and basic inquiries, freeing staff to focus on complex patient needs' },
                    { title: 'Improved Patient Matching', desc: 'AI-powered recommendations ensure patients see the most appropriate specialist for their condition' },
                    { title: 'Enhanced Patient Experience', desc: 'Familiar WhatsApp interface combined with personalized, multilingual support increases patient satisfaction significantly' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300">
                      <span className="text-pink-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-pink-600 to-purple-600 rounded-full"></div>
                  Tech Stack
                </h4>
                <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4">
                  {['ManyChat', 'OpenAI API', 'WhatsApp Business API', 'Google Sheets', 'Bootsheet', 'AI Recommendations', 'Multilingual NLP', 'Automated Notifications'].map((tech) => (
                    <span key={tech} className="px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-pink-600/20 to-purple-600/20 border border-pink-500/30 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold text-white hover:scale-105 transition-transform duration-300 hover:shadow-lg hover:shadow-pink-500/30">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Project 6: UET AI Chatbot */}
          <div className="bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 mb-6 sm:mb-8 md:mb-10 hover:shadow-2xl hover:shadow-purple-500/20">
            <div className="mb-6 sm:mb-8 md:mb-10">
              <span className="inline-block bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-semibold text-white mb-4 sm:mb-6">Featured Project</span>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight">
                UET AI Chatbot - RAG Based University Assistant
              </h3>
            </div>

{/* Project Video Gallery - Single Video */}
<div className="mb-8 sm:mb-10 md:mb-12">
  <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 max-w-4xl mx-auto">
    <video 
      className="w-full h-auto rounded-xl sm:rounded-2xl"
      controls
      autoPlay
      muted
      loop
      playsInline
      poster="/project-thumbnail.jpg"
      style={{ maxHeight: '500px', objectFit: 'contain' }}
    >
      <source src="uet-bot.mp4" type="video/mp4" />
      <source src="/project-demo.webm" type="video/webm" />
      Your browser does not support the video tag.
    </video>
  </div>
</div>

            <div className="space-y-6 sm:space-y-8 md:space-y-10">
              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
                  Background
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  University students constantly need information about courses, administration procedures, societies, notifications, rules, and regulations. Traditional methods of finding this information—browsing multiple websites, waiting for office hours, or searching through lengthy documents—are frustratingly time-consuming. Students need instant, accurate answers to university-specific questions that generic AI assistants like ChatGPT simply cannot provide because they lack access to university-specific data and real-time information.
                </p>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
                  What I Built
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed text-justify">
                  A sophisticated RAG-based AI chatbot specifically trained on University of Engineering and Technology's official dataset, deployed on AWS for 24/7 availability. Built with Spring Boot and powered by OpenAI's advanced APIs, the chatbot provides intelligent, context-aware assistance through both text and voice interfaces, answering university-specific questions that extend far beyond general AI knowledge while also retrieving real-time information from the web when needed.
                </p>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
                  How It Works
                </h4>
                <div className="bg-black/50 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-10 space-y-3 sm:space-y-4 md:space-y-6 border border-purple-500/20">
                  {[
                    { num: '1', title: 'Multi-Modal Input', desc: 'Students interact via text or voice input, with Whisper API converting speech to text for seamless voice queries.' },
                    { num: '2', title: 'Query Analysis', desc: 'System analyzes the question to determine if it requires university-specific knowledge or real-time web information.' },
                    { num: '3', title: 'RAG Knowledge Retrieval', desc: 'OpenAI Vector Database searches through university\'s official dataset for relevant information about courses, rules, societies, and administration.' },
                    { num: '4', title: 'Web Scraping Integration', desc: 'For real-time queries, the system scrapes current information from university websites and relevant online sources.' },
                    { num: '5', title: 'Contextual Response Generation', desc: 'Chat Completion API synthesizes retrieved information into coherent, contextually appropriate answers.' },
                    { num: '6', title: 'Voice Output', desc: 'TTS API converts text responses to natural speech for users who prefer voice interaction.' },
                    { num: '7', title: 'Concurrent User Handling', desc: 'Spring Boot architecture ensures multiple students can interact simultaneously without performance degradation.' }
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3 sm:gap-4 md:gap-5 group hover:translate-x-2 transition-transform duration-300">
                      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-bold min-w-[40px] sm:min-w-[50px] text-center text-base sm:text-lg shadow-lg flex-shrink-0">{step.num}</div>
                      <p className="text-sm sm:text-base text-gray-300"><span className="font-semibold text-white text-base sm:text-lg">{step.title}:</span> {step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
                  Technical Highlights
                </h4>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: 'RAG Architecture', desc: 'Combines retrieval-augmented generation with vector database for accurate, university-specific responses' },
                    { title: 'Multi-Modal Interface', desc: 'Seamlessly handles both text and voice interactions using Whisper and TTS APIs' },
                    { title: 'Hybrid Knowledge System', desc: 'Intelligently combines custom training data with real-time web scraping for comprehensive answers' },
                    { title: 'Enterprise Deployment', desc: 'AWS hosting with Docker containerization ensures scalability, reliability, and 24/7 uptime' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300 hover:text-white transition-colors">
                      <span className="text-indigo-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
                  Outcome
                </h4>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 sm:mb-6 text-justify">
                  The chatbot revolutionizes how students access university information, providing instant, accurate answers that would otherwise require extensive searching or waiting. It delivers:
                </p>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    { title: 'Instant Information Access', desc: 'Students get immediate answers to university-specific questions 24/7, eliminating wait times and reducing administrative burden' },
                    { title: 'Superior Accuracy', desc: 'Custom training on official university data ensures responses are accurate and relevant, far exceeding generic AI capabilities' },
                    { title: 'Enhanced Accessibility', desc: 'Voice interaction makes the system accessible to all students, including those who prefer or require audio interfaces' },
                    { title: 'Scalable Support', desc: 'Handles hundreds of simultaneous queries without performance degradation, serving the entire student body efficiently' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-sm sm:text-base text-gray-300">
                      <span className="text-indigo-500 text-lg sm:text-xl md:text-2xl mt-0.5 flex-shrink-0">▸</span>
                      <span><span className="font-semibold text-white text-sm sm:text-base">{item.title}:</span> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
                  Tech Stack
                </h4>
                <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4">
                  {['Java', 'Spring Boot', 'OpenAI APIs', 'Whisper API', 'TTS API', 'Chat Completion API', 'OpenAI RAG', 'Vector Database', 'Docker', 'AWS', 'HTML/CSS/JavaScript'].map((tech) => (
                    <span key={tech} className="px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border border-indigo-500/30 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold text-white hover:scale-105 transition-transform duration-300 hover:shadow-lg hover:shadow-indigo-500/30">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 sm:py-16 md:py-24 lg:py-32 px-4 sm:px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-950/10 to-black"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="mb-10 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 md:mb-6">
              Let's <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">Connect</span>
            </h2>
            <div className="w-20 sm:w-24 h-1.5 sm:h-2 bg-gradient-to-r from-purple-600 to-blue-600 mx-auto rounded-full mb-6 sm:mb-8"></div>
            <p className="text-sm sm:text-base md:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed px-4 text-center sm:text-justify">
              Interested in AI automation and intelligent systems? Let's discuss how we can work together.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {[
              { icon: Mail, label: 'Email', value: 'hussnainjafari176@gmail.com', href: 'mailto:hussnainjafari176@gmail.com', gradient: 'from-purple-600 to-pink-600' },
              { icon: Phone, label: 'WhatsApp', value: '+92 336 5096207', href: 'https://wa.me/923365096207', gradient: 'from-green-600 to-emerald-600' },
              { icon: Linkedin, label: 'LinkedIn', value: 'Connect with me', href: 'https://www.linkedin.com/in/syed-ali-hussnain-shah-9b0131300/', gradient: 'from-blue-600 to-cyan-600' },
              { icon: Github, label: 'GitHub', value: 'View my code', href: 'https://github.com/jafari176', gradient: 'from-gray-600 to-gray-800' }
            ].map((contact, idx) => (
              <a 
                key={idx} 
                href={contact.href} 
                target={contact.href.startsWith('http') ? '_blank' : undefined} 
                rel={contact.href.startsWith('http') ? 'noopener noreferrer' : undefined} 
                className="group bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl px-4 sm:px-6 md:px-8 py-6 sm:py-8 rounded-2xl sm:rounded-3xl border border-gray-800 hover:border-purple-500/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20"
              >
                <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-5">
                  <div className={`p-3 sm:p-4 bg-gradient-to-br ${contact.gradient} rounded-xl sm:rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <contact.icon className="text-white w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-base sm:text-lg text-gray-500 mb-1">{contact.label}</p>
                    <p className="text-base sm:text-lg md:text-xl text-white font-semibold group-hover:text-purple-400 transition-colors break-all">{contact.value}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 md:py-10 px-4 sm:px-6 border-t border-purple-500/20">
        <div className="max-w-7xl mx-auto text-center text-gray-400">
          <p className="text-base sm:text-lg">© 2024 Ali Hussnain. All rights reserved.</p>
          <p className="mt-2 sm:mt-3 text-sm sm:text-base">Built with React & Tailwind CSS</p>
        </div>
      </footer>
    </div>
  );
}